import { DestroyRef, Injectable } from '@angular/core'
import { CasinoGamesWsApiWsApi, CasinoVirtualSubCategory } from '@/api/websocket/casino-games-ws-api'
import { LayoutService } from '@/services/layout.service'
import { CASINO_CATEGORIES, CasinoGameItem } from '@/features/casino/casino.model'
import { toCasinoGameItems } from '@/features/casino/casino.mapper'
import { SearchView } from '../search.model'
import { toCasinoSearchView } from '../casino-search.mapper'
import { SearchSource } from '../search.source'

// The API's ceiling per page. Search shows the first page only; paging is the
// casino filter's, which answers with the same list.
const SEARCH_PAGE_SIZE = 20

/**
 * Casino games, through the API's search call rather than the lobby's gameName
 * filter: search is its own list now, not a narrowed lobby. It takes no
 * category, so a game is found from any casino section.
 */
@Injectable({ providedIn: 'root' })
export class CasinoSearchSource implements SearchSource {
	constructor(
		private casinoGamesWsApiWsApi: CasinoGamesWsApiWsApi,
		private layoutService: LayoutService,
		private destroyRef: DestroyRef
	) {}

	// Asked once a session per device type: a row of suggestions need not be live.
	private readonly suggested = new Map<boolean, Promise<CasinoGameItem[]>>()

	/**
	 * Suggested Games for the panel's empty state: the main casino's Recommended
	 * row. Empty when the visitor has none or the call fails; the row then hides.
	 */
	public suggestedGames(): Promise<CasinoGameItem[]> {
		const mobile = this.layoutService.isMobile()
		let pending = this.suggested.get(mobile)

		if (!pending) {
			pending = this.casinoGamesWsApiWsApi.getCasinoGamesByVirtualSubCategory({
				gcId: CASINO_CATEGORIES.casino,
				gsId: CasinoVirtualSubCategory.recommended,
				mobile,
				fromIndex: 0
			}, this.destroyRef)
				.then((category) => toCasinoGameItems(
					category.subCategories?.find((subCategory) =>
						subCategory.gsId === CasinoVirtualSubCategory.recommended)?.games ?? []
				))
				.catch(() => {
					// Not cached, so the next opening tries again.
					this.suggested.delete(mobile)
					return []
				})
			this.suggested.set(mobile, pending)
		}

		return pending
	}

	public async search(term: string): Promise<SearchView> {
		const result = await this.casinoGamesWsApiWsApi.searchCasinoGames({
			mobile: this.layoutService.isMobile(),
			searchText: term,
			fromIndex: 0,
			pageSize: SEARCH_PAGE_SIZE
		}, this.destroyRef)

		return toCasinoSearchView(result)
	}
}
